import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface WhatsappStatus {
    connected: boolean;
    user?: {
        name: string;
        id: string;
    };
    qr?: string | null;
}

const WhatsappSettings: React.FC = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState<WhatsappStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [polling, setPolling] = useState(false);

    const API_URL = import.meta.env.VITE_WHATSAPP_API_URL || '/api/whatsapp';
    const API_KEY = 'hedui2026';

    const fetchStatus = async () => {
        try {
            const res = await fetch(`${API_URL}/status`, {
                headers: { 'x-api-key': API_KEY }
            });
            const data = await res.json();
            if (data.success) {
                setStatus(data.data);
            }
        } catch (error) {
            console.error('Failed to fetch WhatsApp status', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();

        // Poll every 3 seconds if not connected to wait for QR code scan
        const interval = setInterval(() => {
            if (!status?.connected) {
                setPolling(true);
                fetchStatus().finally(() => setPolling(false));
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [status?.connected]);

    const handleDisconnect = async () => {
        if (!window.confirm('确定要断开连接吗？断开后将无法自动发送异常通知。')) return;

        const loadingToast = toast.loading('正在断开连接...');
        try {
            const res = await fetch(`${API_URL}/disconnect`, {
                method: 'POST',
                headers: { 'x-api-key': API_KEY }
            });
            if (res.ok) {
                toast.success('已断开连接', { id: loadingToast });
                fetchStatus();
            } else {
                toast.error('断开连接失败', { id: loadingToast });
            }
        } catch (error) {
            toast.error('请求失败', { id: loadingToast });
        }
    };

    return (
        <div className="flex flex-col h-full bg-background-light dark:bg-background-dark font-display text-slate-800 dark:text-slate-100">
            <header className="bg-surface-dark/95 backdrop-blur shadow-md z-50 sticky top-0 flex-shrink-0">
                <div className="px-4 py-4 flex items-center justify-between relative border-b border-white/5">
                    <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-white transition-colors">
                        <span className="material-icons">arrow_back_ios</span>
                    </button>
                    <h1 className="text-lg font-bold text-white tracking-wide ml-8">WhatsApp 通知设置</h1>
                    <div className="w-8"></div>
                </div>
            </header>

            <main className="flex-1 p-5 overflow-y-auto no-scrollbar pb-32">
                <div className="bg-surface-dark rounded-2xl border border-white/5 p-6 shadow-xl relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 -mr-6 -mt-6 rounded-full w-32 h-32 bg-green-500/10 blur-2xl pointer-events-none"></div>

                    <div className="flex items-center gap-4 mb-6 relative z-10">
                        <div className="w-14 h-14 bg-green-500/10 rounded-2xl flex items-center justify-center p-2 border border-green-500/20 shadow-inner">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" className="w-full h-full object-contain" alt="WA" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">通知机器人状态</h2>
                            <p className="text-xs text-gray-400 mt-1">
                                绑定后，所有运货重量异常情况都会实时推送
                            </p>
                        </div>
                    </div>

                    <div className="bg-background-dark rounded-xl p-5 border border-white/5">
                        {loading && !status ? (
                            <div className="flex flex-col items-center justify-center py-10 opacity-70">
                                <span className="material-icons animate-spin text-3xl mb-3 text-green-500">sync</span>
                                <p className="text-sm font-medium text-gray-400">正在检查连接状态...</p>
                            </div>
                        ) : status?.connected ? (
                            <div className="flex flex-col items-center justify-center py-6 animate-in fade-in zoom-in duration-300">
                                <div className="w-20 h-20 bg-green-500/20 border-4 border-green-500/30 rounded-full flex items-center justify-center mb-4">
                                    <span className="material-icons text-4xl text-green-500">check_circle</span>
                                </div>
                                <h3 className="text-2xl font-black text-green-400 tracking-wider mb-2">已成功连接</h3>
                                <div className="bg-surface-dark px-4 py-2 rounded-lg border border-white/10 mb-6 flex flex-col items-center">
                                    <span className="text-xs text-gray-400 uppercase tracking-widest font-bold mb-1">当前绑定设备</span>
                                    <span className="text-sm font-mono text-gray-200">
                                        {status?.user?.id?.split(':')[0] || 'Unknown Number'}
                                    </span>
                                    {status?.user?.name && (
                                        <span className="text-xs text-gray-500 mt-1">{status?.user.name}</span>
                                    )}
                                </div>
                                <button
                                    onClick={handleDisconnect}
                                    className="px-6 py-2.5 bg-red-500/10 text-red-500 border border-red-500/20 rounded-xl font-bold text-sm hover:bg-red-500 hover:text-white transition-all active:scale-95 flex items-center gap-2"
                                >
                                    <span className="material-icons text-sm">link_off</span>
                                    取消绑定
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center relative">
                                {status?.qr ? (
                                    <div className="bg-white p-4 rounded-2xl shadow-lg border-4 border-green-500/20 mb-6 animate-in slide-in-from-bottom-2 duration-300">
                                        <img src={status.qr} alt="WhatsApp QR Code" className="w-48 h-48 object-contain" />
                                    </div>
                                ) : (
                                    <div className="w-48 h-48 bg-surface-hover/50 rounded-2xl border-2 border-dashed border-gray-600 flex flex-col justify-center items-center mb-6">
                                        <span className="material-icons text-4xl text-gray-500 mb-2 animate-pulse">qr_code</span>
                                        <span className="text-xs text-gray-500">正在生成二维码...</span>
                                    </div>
                                )}

                                <div className="w-full">
                                    <h3 className="text-sm font-bold text-green-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <span className="material-icons text-sm">info</span>
                                        连接步骤
                                    </h3>
                                    <ol className="space-y-3 relative before:absolute before:inset-y-0 before:left-[11px] before:w-0.5 before:bg-white/5">
                                        {[
                                            '打开将用于发送通知的 WhatsApp 手机应用',
                                            '点击右上角菜单 ( ⋮ )',
                                            '选择 "已关联的设备" (Linked devices)',
                                            '点击 "关联设备" (Link a device) 并扫描上方二维码'
                                        ].map((step, idx) => (
                                            <li key={idx} className="flex gap-4 items-start relative z-10">
                                                <div className="w-6 h-6 rounded-full bg-surface-dark border-2 border-green-500/50 flex items-center justify-center text-[10px] font-black text-green-500 shrink-0 mt-0.5">
                                                    {idx + 1}
                                                </div>
                                                <p className="text-sm text-gray-300 leading-snug">{step}</p>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                {polling && (
                                    <div className="absolute top-2 right-2 flex items-center gap-1 opacity-50">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                        <span className="text-[9px] text-green-500 uppercase tracking-widest font-bold">监听中</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {!loading && status?.connected && (
                    <div className="mt-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
                        <span className="material-icons text-blue-500 mt-0.5">campaign</span>
                        <div className="text-sm text-blue-100/80 leading-relaxed">
                            <strong className="text-blue-400 block mb-1">提示：关于通知开关</strong>
                            此 WhatsApp 服务目前固定发送至全局指定的接收人。只要此机器人保持登录状态，任何包裹出现严重重量偏差均会自动化推送提醒。
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default WhatsappSettings;
